/**
 * Which document to look at first, out of a hundred.
 *
 * A batch returns everything at once and says nothing about where to start.
 * Below about ten documents that is fine, because a person can read the whole
 * list; at a hundred it turns a verified batch back into an unsorted pile,
 * which is the state the product exists to get someone out of.
 *
 * The order is worst first, and "worst" is three questions asked in sequence:
 *
 * 1. **Could it be traversed at all.** A FAIL has no geometry, so no closure
 *    number exists to compare, and it is categorically worse than any REVIEW.
 *    Sorting it by a closure ratio it does not have would bury it.
 * 2. **How badly does it close.** Smaller denominator is worse: 1:848 wants
 *    eyes long before 1:96,419 does. A traverse that closes exactly carries a
 *    null denominator, which means best rather than missing, and the two nulls
 *    are told apart by whether the geometry exists at all.
 * 3. **What was the least confident call.** The extractor's own floor across
 *    every course, because a document that closes well on courses it was
 *    unsure of is a different risk from one that closes well on courses it
 *    read cleanly.
 *
 * No thresholds live here. The server already decided PASS, REVIEW and FAIL;
 * this only decides reading order, and a second opinion about what counts as
 * good would be a verdict competing with the one that ships.
 */

import type { HeldResponse, ServerVerdict, Tract, Verdict } from "./types";

/** Worst-first ranking. Lower sorts earlier. */
const STATUS_RANK: Record<string, number> = { FAIL: 0, REVIEW: 1, PASS: 2 };
const CONFIDENCE_RANK: Record<string, number> = {
  low: 0,
  medium: 1,
  high: 2,
};

export type Confidence = "low" | "medium" | "high";

export interface Triaged {
  item: HeldResponse;
  document: string;
  /** The document's worst tract verdict, or its document-level one. */
  status: Verdict;
  /**
   * Worst closure denominator across the document's tracts. Null means no
   * tract could be traversed, which is worse than any number; Infinity means
   * every traverse closed exactly.
   */
  closure: number | null;
  /** The least confident call anywhere in the document. */
  confidence: Confidence | null;
  /** Why the server flagged it, for the queue to show without opening it. */
  reasons: string[];
  tracts: number;
}

/** Every document in the batch, worst first. */
export function triage(batch: HeldResponse[]): Triaged[] {
  return batch.map(summarize).sort(compare);
}

/** The ones a person still has to look at. */
export function needsReview(rows: Triaged[]): Triaged[] {
  return rows.filter((row) => row.status !== "PASS");
}

function summarize(item: HeldResponse): Triaged {
  const verdicts: ServerVerdict[] = item.parsed.groundtruth_verdicts ?? [];
  const tracts: Tract[] = item.parsed.extraction?.tracts ?? [];
  const documentVerdict = item.parsed.document_verdict;

  // A plat or an as-built produces no tracts and speaks through the
  // document-level verdict instead, so a queue that only read the array would
  // show those as clean.
  const statuses = verdicts.map((v) => v.status);
  if (documentVerdict?.status) statuses.push(documentVerdict.status as Verdict);

  const reasons = [
    ...verdicts.flatMap((v) => (v.status === "PASS" ? [] : (v.reasons ?? []))),
    ...(documentVerdict?.reasons ?? []),
  ];

  return {
    item,
    document: item.parsed.document ?? "(unnamed document)",
    status: worstStatus(statuses),
    closure: worstClosure(verdicts),
    confidence: confidenceFloor(tracts),
    reasons,
    tracts: verdicts.length,
  };
}

function worstStatus(statuses: Verdict[]): Verdict {
  if (!statuses.length) return "REVIEW"; // nothing judged is not the same as nothing wrong
  return statuses.reduce((worst, status) =>
    (STATUS_RANK[status] ?? 1) < (STATUS_RANK[worst] ?? 1) ? status : worst,
  );
}

/**
 * The worst closure in the document.
 *
 * Null when no tract has geometry: the traverse could not be computed, so
 * there is no ratio, and that outranks any bad ratio. Infinity when every
 * traverse closed exactly, since `closure_denominator` is null in that case
 * and JSON cannot carry Infinity.
 */
function worstClosure(verdicts: ServerVerdict[]): number | null {
  const traversed = verdicts.filter((v) => v.geometry);
  if (!traversed.length) return null;
  return traversed.reduce((worst, v) => {
    const denominator = v.geometry?.closure_denominator ?? Infinity;
    return Math.min(worst, denominator);
  }, Infinity);
}

function confidenceFloor(tracts: Tract[]): Confidence | null {
  let floor: Confidence | null = null;
  for (const tract of tracts) {
    for (const call of [...(tract.calls ?? []), ...(tract.tie_calls ?? [])]) {
      const value = call.confidence as Confidence | undefined;
      if (!value || !(value in CONFIDENCE_RANK)) continue;
      if (floor === null || CONFIDENCE_RANK[value] < CONFIDENCE_RANK[floor]) {
        floor = value;
      }
    }
  }
  return floor;
}

function compare(a: Triaged, b: Triaged): number {
  const byStatus = (STATUS_RANK[a.status] ?? 1) - (STATUS_RANK[b.status] ?? 1);
  if (byStatus !== 0) return byStatus;

  // Null is "nothing traversed", which is worse than any number, so it sorts
  // ahead rather than falling to the end the way a missing value would.
  if (a.closure === null || b.closure === null) {
    if (a.closure === b.closure) return byConfidence(a, b);
    return a.closure === null ? -1 : 1;
  }
  if (a.closure !== b.closure) return a.closure - b.closure;

  return byConfidence(a, b);
}

function byConfidence(a: Triaged, b: Triaged): number {
  const rank = (value: Confidence | null) =>
    value === null ? CONFIDENCE_RANK.high + 1 : CONFIDENCE_RANK[value];
  const byFloor = rank(a.confidence) - rank(b.confidence);
  if (byFloor !== 0) return byFloor;
  // Stable and readable when everything else ties.
  return a.document.localeCompare(b.document);
}

/** The closure ratio as the review panel writes it, for the queue row. */
export function closureLabel(row: Triaged): string {
  if (row.closure === null) return "not traversable";
  if (row.closure === Infinity) return "closes exactly";
  return `1:${Math.round(row.closure).toLocaleString()}`;
}
